import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TRIAGE_OUTCOMES,
  triageBlockers,
  WRITE_ROLES,
  type Engagement,
  type FindingSuggestion,
  type Finding,
  type FindingStatus,
  type Severity,
} from '@ai-secure-twin/shared';
import { IntelPanel } from './IntelPanel';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../auth/auth';
import { latestRun, ScoreBadge, suggestionFor, useAgentRuns } from './AiSuggestion';

const ORDER: Severity[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFORMATIONAL'];

const severityClass = (s: Severity) => `sev-${s.toLowerCase()}`;

export const STATUS_LABEL: Record<FindingStatus, string> = {
  UNVERIFIED: 'Unverified',
  CONFIRMED: 'Confirmed',
  FALSE_POSITIVE: 'False positive',
  ACCEPTED_RISK: 'Accepted risk',
  FIXED: 'Fixed',
};

/**
 * Findings from real scans. Everything shown here came from a target, so nothing is rendered as
 * markup.
 *
 * In TRIAGE each finding can be decided: confirmed, a false positive, an accepted risk, fixed, or
 * reopened. Every decision needs a reason, because the report that follows is read by people
 * who will want to know why a finding was dismissed.
 */
export function FindingsList({ engagement }: { engagement: Engagement }) {
  const { user } = useAuth();
  const [open, setOpen] = useState<string | null>(null);
  const [intelOpen, setIntelOpen] = useState<string | null>(null);
  const mayWrite = user ? WRITE_ROLES.has(user.role) : false;

  const findings = useQuery({
    queryKey: ['findings', engagement.id],
    queryFn: () => api.listFindings(engagement.id),
  });

  const items = [...(findings.data?.items ?? [])].sort(
    (a, b) => ORDER.indexOf(a.severity) - ORDER.indexOf(b.severity),
  );
  // The newest triage suggestions, if the agent has been asked for them.
  const runs = useAgentRuns(engagement.id);
  const triageRun = latestRun(runs.data?.items, ['SUGGEST_TRIAGE']);
  /** Suggestions are only offered for findings still awaiting a decision. */
  const suggestion = (f: Finding) =>
    f.status === 'UNVERIFIED' ? suggestionFor(triageRun, f.id) : undefined;

  const triaging = engagement.stage === 'TRIAGE';
  const mayTriage = triaging && (user ? WRITE_ROLES.has(user.role) : false);
  const unverified = items.filter((f) => f.status === 'UNVERIFIED').length;

  return (
    <section className="card panel table-wrap" aria-labelledby="find-title">
      <div className="panel-head">
        <h2 id="find-title" className="panel-title">
          Findings
        </h2>
        {triaging && items.length > 0 ? (
          <span className={`status ${unverified > 0 ? 'status-waiting' : 'status-done'}`}>
            {unverified > 0 ? `${unverified} of ${items.length} to decide` : 'All decided'}
          </span>
        ) : null}
      </div>

      {triaging && items.length > 0 && unverified > 0 ? (
        <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
          Decide each finding, with a reason. The report can be submitted once none is left
          unverified.
        </p>
      ) : null}

      {findings.isLoading ? (
        <p className="muted">Loading findings…</p>
      ) : items.length === 0 ? (
        <p className="muted">
          No findings. Nothing has been scanned yet, or the last scan found nothing to report.
        </p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Severity</th>
              <th>Finding</th>
              <th>Where</th>
              <th>Status</th>
              {mayTriage ? <th aria-label="Decide" /> : <th>Last seen</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((f) => (
              <Fragment key={f.id}>
                <tr>
                  <td className={severityClass(f.severity)}>{f.severity}</td>
                  <td>
                    {f.title}
                    {f.ruleId ? (
                      <div className="mono" style={{ fontSize: 11, color: 'var(--text-subtle)' }}>
                        rule {f.ruleId}
                        {f.classification?.cweId ? ` · CWE-${f.classification.cweId}` : ''}
                        {f.classification?.cveIds.length
                          ? ` · ${f.classification.cveIds.length} CVE`
                          : ''}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="link-btn intel-toggle"
                      aria-expanded={intelOpen === f.id}
                      onClick={() => setIntelOpen(intelOpen === f.id ? null : f.id)}
                    >
                      {intelOpen === f.id ? 'Hide intelligence' : 'Intelligence'}
                    </button>
                  </td>
                  <td className="mono muted" style={{ fontSize: 12, wordBreak: 'break-all' }}>
                    {f.evidence?.url ?? '—'}
                  </td>
                  <td>
                    <span className={`finding-status fs-${f.status.toLowerCase()}`}>
                      {STATUS_LABEL[f.status]}
                    </span>
                    {f.lastTriage ? (
                      <div
                        className="muted"
                        style={{ fontSize: 11, marginTop: 4 }}
                        title={f.lastTriage.reason}
                      >
                        by {f.lastTriage.byEmail ?? 'a teammate'}
                      </div>
                    ) : null}
                    {triaging && suggestion(f) ? (
                      <div className="ai-row-hint">
                        <span className="ai-tag">AI</span>{' '}
                        {STATUS_LABEL[suggestion(f)!.suggestedStatus]} · {suggestion(f)!.confidence}
                        /10
                      </div>
                    ) : null}
                  </td>
                  {mayTriage ? (
                    <td>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        aria-expanded={open === f.id}
                        onClick={() => setOpen(open === f.id ? null : f.id)}
                      >
                        {open === f.id ? 'Close' : f.status === 'UNVERIFIED' ? 'Decide' : 'Change'}
                      </button>
                    </td>
                  ) : (
                    <td className="muted">{new Date(f.lastSeenAt).toLocaleDateString()}</td>
                  )}
                </tr>
                {intelOpen === f.id ? (
                  <tr className="detail-row">
                    <td colSpan={5}>
                      <IntelPanel
                        engagementId={engagement.id}
                        findingId={f.id}
                        mayRefresh={mayWrite}
                        ai={
                          triageRun && suggestion(f)?.analysis
                            ? {
                                analysis: suggestion(f)!.analysis!,
                                confidence: suggestion(f)!.confidence,
                                promptVersion: triageRun.result?.promptVersion ?? '',
                              }
                            : undefined
                        }
                      />
                    </td>
                  </tr>
                ) : null}
                {open === f.id ? (
                  <tr className="detail-row">
                    <td colSpan={5}>
                      <TriageForm
                        engagement={engagement}
                        finding={f}
                        suggestion={
                          triageRun && suggestion(f)
                            ? { runId: triageRun.id, ...suggestion(f)! }
                            : undefined
                        }
                        onDone={() => setOpen(null)}
                      />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function TriageForm({
  engagement,
  finding,
  suggestion,
  onDone,
}: {
  engagement: Engagement;
  finding: Finding;
  /** The agent's suggestion for this finding, and the run it came from. */
  suggestion: (FindingSuggestion & { runId: string }) | undefined;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const choices = TRIAGE_OUTCOMES.filter((s) => s !== finding.status);
  const [to, setTo] = useState<FindingStatus>(choices[0]!);
  const [reason, setReason] = useState('');

  const decide = useMutation({
    mutationFn: () =>
      api.triageFinding(engagement.id, finding.id, {
        from: finding.status,
        to,
        reason: reason.trim(),
        /*
         * Sent whenever a suggestion was on screen, used or not: it was part of what the person
         * saw when deciding. The server reads what it said and records whether it was followed.
         */
        ...(suggestion ? { suggestionRunId: suggestion.runId } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['findings', engagement.id] });
      onDone();
    },
  });

  const blockers = triageBlockers(engagement.stage, finding.status, finding.status, to);
  const reasonShort = reason.trim().length < 10;

  return (
    <form
      className="triage-form"
      onSubmit={(e) => {
        e.preventDefault();
        decide.mutate();
      }}
    >
      {finding.description ? <p className="muted triage-context">{finding.description}</p> : null}
      {finding.lastTriage ? (
        <p className="triage-previous">
          Last decided <strong>{STATUS_LABEL[finding.lastTriage.to]}</strong> by{' '}
          {finding.lastTriage.byEmail ?? 'a teammate'} on{' '}
          {new Date(finding.lastTriage.at).toLocaleDateString()}: “{finding.lastTriage.reason}”
        </p>
      ) : null}

      {suggestion ? (
        <div className="ai-advice">
          <div className="ai-advice-head">
            <span className="ai-tag">AI suggestion</span>
            <ScoreBadge score={suggestion.confidence} />
          </div>
          <p className="ai-advice-body">
            <strong>{STATUS_LABEL[suggestion.suggestedStatus]}</strong> — {suggestion.reason}
          </p>
          <div className="ai-advice-actions">
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => {
                setTo(suggestion.suggestedStatus);
                setReason(suggestion.reason);
              }}
            >
              Use this suggestion
            </button>
            <span className="ai-advice-note">
              Fills in the form for you to check and edit. You still record the decision, and the
              record notes the AI suggested it.
            </span>
          </div>
        </div>
      ) : null}

      <fieldset className="triage-outcomes">
        <legend>Decision</legend>
        {choices.map((s) => (
          <label key={s} className="triage-choice">
            <input
              type="radio"
              name={`triage-${finding.id}`}
              value={s}
              checked={to === s}
              onChange={() => setTo(s)}
            />
            {s === 'UNVERIFIED' ? 'Reopen as unverified' : STATUS_LABEL[s]}
          </label>
        ))}
      </fieldset>

      <div className="field">
        <label htmlFor={`reason-${finding.id}`}>Reason</label>
        <textarea
          id={`reason-${finding.id}`}
          className="input"
          rows={3}
          maxLength={1000}
          required
          placeholder="What you checked, and what it showed. This goes in the report."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <span className="muted" style={{ fontSize: 11 }}>
          At least 10 characters. Recorded with your name and kept permanently.
        </span>
      </div>

      {blockers.length > 0 ? <p className="muted">{blockers.join(' ')}</p> : null}
      {decide.error ? (
        <p className="login-error" role="alert">
          {decide.error instanceof ApiError
            ? decide.error.message
            : 'Could not record the decision.'}
        </p>
      ) : null}

      <div className="actions">
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={decide.isPending || blockers.length > 0 || reasonShort}
        >
          {decide.isPending ? 'Recording…' : 'Record decision'}
        </button>
        <button type="button" className="btn btn-outline btn-sm" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}
